import React from "react";
import { Clock } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");

export const useCountdown = (target) => {
  const targetMs = target ? new Date(target).getTime() : 0;
  const calc = React.useCallback(() => {
    const diff = Math.max(0, targetMs - Date.now());
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { diff, days, hours, minutes, seconds };
  }, [targetMs]);

  const [time, setTime] = React.useState(calc);

  React.useEffect(() => {
    setTime(calc());
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [calc]);

  return time;
};

const Unit = ({ value, label, dark }) => (
  <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
    <span
      className={`font-display font-black tabular-nums leading-none tracking-tight ${
        dark ? "text-sand-light" : "text-ink"
      } text-2xl sm:text-3xl`}
    >
      {pad(value)}
    </span>
    <span className={`text-[9px] sm:text-[10px] uppercase tracking-[0.2em] mt-1 ${dark ? "text-sand-light/50" : "text-ink/45"}`}>
      {label}
    </span>
  </div>
);

const Sep = ({ dark }) => (
  <span className={`font-display font-black text-2xl sm:text-3xl leading-none -mt-2 ${dark ? "text-ochre/60" : "text-clay/40"}`}>:</span>
);

// Compact inline digits for the banner
export const CountdownInline = ({ target }) => {
  const t = useCountdown(target);
  if (!target || t.diff <= 0) return null;
  const cell = (v, l) => (
    <span className="inline-flex items-baseline gap-0.5">
      <b className="font-black tabular-nums">{pad(v)}</b>
      <span className="text-[10px] opacity-70">{l}</span>
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 font-display" data-testid="countdown-inline">
      {cell(t.days, "d")}
      <span className="opacity-40">:</span>
      {cell(t.hours, "h")}
      <span className="opacity-40">:</span>
      {cell(t.minutes, "m")}
      <span className="opacity-40">:</span>
      {cell(t.seconds, "s")}
    </span>
  );
};

// Prominent countdown card for the hero
export const HeroCountdown = ({ target }) => {
  const t = useCountdown(target);
  if (!target || t.diff <= 0) return null;
  return (
    <div
      className="mt-8 max-w-md rounded-2xl bg-ink text-sand-light p-5 shadow-[0_16px_40px_rgba(26,28,41,0.22)] grain"
      data-testid="hero-countdown"
    >
      <div className="flex items-center gap-2 text-ochre text-xs font-bold uppercase tracking-[0.2em]">
        <Clock size={14} strokeWidth={2.4} /> Free for everyone ends in
      </div>
      <div className="flex items-start justify-between mt-4 px-1">
        <Unit value={t.days} label="Days" dark />
        <Sep dark />
        <Unit value={t.hours} label="Hours" dark />
        <Sep dark />
        <Unit value={t.minutes} label="Mins" dark />
        <Sep dark />
        <Unit value={t.seconds} label="Secs" dark />
      </div>
      <p className="text-xs text-sand-light/55 mt-4 leading-relaxed">
        Enjoy full access free until <span className="text-sand-light/80 font-semibold">1 January 2027</span>.
        After that, premium features move to ₦2,500/mo with a 30-day free trial.
      </p>
    </div>
  );
};
